import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslatePipe } from './translate.pipe';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <nav class="pager" [attr.aria-label]="'common.pagination' | t">
      <div class="pager-summary">
        <span class="pager-range">{{ 'common.showingRange' | t:{ start: pageStart, end: pageEnd, total: total } }}</span>
        <label class="pager-size">
          {{ 'common.rowsPerPage' | t }}
          <select [ngModel]="pageSize" (ngModelChange)="changePageSize($event)">
            <option *ngFor="let option of pageSizes" [ngValue]="option">{{ option }}</option>
          </select>
        </label>
      </div>
      <div class="pager-actions">
        <button type="button" class="secondary pager-first" [disabled]="safePage <= 1" (click)="selectPage(1)">{{ 'common.first' | t }}</button>
        <button type="button" class="secondary pager-previous" [disabled]="safePage <= 1" (click)="selectPage(safePage - 1)">{{ 'common.previous' | t }}</button>
        <button
          type="button"
          class="secondary page-number"
          *ngFor="let pageNumber of visiblePages"
          [class.active]="pageNumber === safePage"
          [attr.aria-current]="pageNumber === safePage ? 'page' : null"
          (click)="selectPage(pageNumber)">
          {{ pageNumber }}
        </button>
        <span class="pager-page-summary">{{ 'lifecycle.pageOf' | t:{ page: safePage, total: totalPages } }}</span>
        <button type="button" class="secondary pager-next" [disabled]="safePage >= totalPages" (click)="selectPage(safePage + 1)">{{ 'common.next' | t }}</button>
        <button type="button" class="secondary pager-last" [disabled]="safePage >= totalPages" (click)="selectPage(totalPages)">{{ 'common.last' | t }}</button>
      </div>
    </nav>
  `
})
export class PaginationComponent {
  @Input() total = 0;
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() pageSizes: number[] = [10, 25, 50, 100];
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  get totalPages(): number {
    return Math.max(1, Math.ceil(Math.max(0, this.total) / Math.max(1, this.pageSize)));
  }

  get pageStart(): number {
    return this.total > 0 ? ((this.safePage - 1) * this.pageSize) + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min(this.safePage * this.pageSize, Math.max(0, this.total));
  }

  get visiblePages(): number[] {
    const total = this.totalPages;
    const current = this.safePage;
    const start = Math.max(1, Math.min(current - 2, total - 4));
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  selectPage(page: number): void {
    const next = Math.min(Math.max(1, Number(page) || 1), this.totalPages);
    if (next !== this.page) this.pageChange.emit(next);
  }

  changePageSize(pageSize: number): void {
    const next = Number(pageSize);
    if (next > 0 && next !== this.pageSize) this.pageSizeChange.emit(next);
  }

  get safePage(): number {
    return Math.min(Math.max(1, this.page), this.totalPages);
  }
}
